import { CitationValues } from "../../../feature-components/WidgetLoader/WidgetContext";

/**
 * Groups consecutive citation widgets that reference the same document (same src).
 * Only runs after message streaming is complete.
 *
 * This function analyzes citation widgets in markdown content and groups consecutive
 * citations from the same source document into a single citation-group widget.
 * This reduces visual clutter and improves UX by showing one icon for multiple
 * citations from the same document.
 *
 * @param content - Markdown content containing ::widget:citation:id:: syntax
 * @param citationState - Optional map of citation IDs to their data (src, title, etc.)
 * @returns Transformed content with grouped citations, or original content if unchanged
 *
 * @example
 * Input content:
 * "text::widget:citation:src-192::::widget:citation:src-72::::widget:citation:src-88::"
 *
 * Citation state:
 * - src-192: { src: "doc-a.pdf", ... }
 * - src-72:  { src: "doc-a.pdf", ... }  // Same src as src-192
 * - src-88:  { src: "doc-b.pdf", ... }  // Different src
 *
 * Output content:
 * "text::widget:citation-group:src-192,src-72::::widget:citation:src-88::"
 *
 * Result: 2 citation icons instead of 3 (first two grouped)
 *
 * @remarks
 * - Only groups strictly consecutive citations (no text between them)
 * - Only groups citations with matching src values
 * - Performance: O(n) where n is content length
 * - Validates citation IDs and logs warnings for invalid formats
 */
export function groupCitationsByDocument(
  content: string,
  citationState?: Record<string, Partial<CitationValues>>,
): string {
  // If no citation state, return content unchanged
  if (!citationState) return content;

  const widgetRegex = /::widget:citation:([\w.-]+)::/g;
  const matches: Array<{
    id: string;
    index: number;
    fullMatch: string;
  }> = [];

  // Find all citation widgets in the content
  let match;
  while ((match = widgetRegex.exec(content)) !== null) {
    const citationId = match[1];

    // Validate citation ID format (matches replaceLegacyCitationPlaceholder pattern)
    if (!/^src-\w+$/.test(citationId)) {
      console.warn(`Invalid citation format: ${citationId}`);
    }

    matches.push({
      id: citationId,
      index: match.index,
      fullMatch: match[0],
    });
  }

  // If less than 2 citations, no grouping needed
  if (matches.length < 2) return content;

  // Find groups of consecutive citations with same src
  const groups: Array<{
    ids: string[];
    startIndex: number;
    endIndex: number;
  }> = [];

  let currentGroup: string[] = [matches[0].id];
  let currentSrc = citationState[matches[0].id]?.src;
  let groupStartIndex = matches[0].index;

  for (let i = 1; i < matches.length; i++) {
    const prevMatch = matches[i - 1];
    const currentMatch = matches[i];
    const currentCitationSrc = citationState[currentMatch.id]?.src;

    // Check if consecutive (no text between them)
    const gapStart = prevMatch.index + prevMatch.fullMatch.length;
    const gapEnd = currentMatch.index;
    const textBetween = content.slice(gapStart, gapEnd);
    const isConsecutive = textBetween === "";

    // Check if same document (same src)
    const isSameDocument =
      currentCitationSrc && currentSrc && currentCitationSrc === currentSrc;

    if (isConsecutive && isSameDocument) {
      // Add to current group
      currentGroup.push(currentMatch.id);
    } else {
      // Finalize current group if it has multiple citations
      if (currentGroup.length > 1) {
        const lastMatchInGroup = matches[i - 1];
        groups.push({
          ids: currentGroup,
          startIndex: groupStartIndex,
          endIndex: lastMatchInGroup.index + lastMatchInGroup.fullMatch.length,
        });
      }

      // Start new group
      currentGroup = [currentMatch.id];
      currentSrc = currentCitationSrc;
      groupStartIndex = currentMatch.index;
    }
  }

  // Finalize last group if it has multiple citations
  if (currentGroup.length > 1) {
    const lastMatch = matches[matches.length - 1];
    groups.push({
      ids: currentGroup,
      startIndex: groupStartIndex,
      endIndex: lastMatch.index + lastMatch.fullMatch.length,
    });
  }

  // If no groups found, return original content
  if (groups.length === 0) return content;

  // Replace grouped citations in content (reverse order to maintain indices)
  let result = content;
  for (let i = groups.length - 1; i >= 0; i--) {
    const group = groups[i];
    const replacementText = `::widget:citation-group:${group.ids.join(",")}::`;

    result =
      result.slice(0, group.startIndex) +
      replacementText +
      result.slice(group.endIndex);
  }

  return result;
}
